import ReactEcharts from "echarts-for-react"
import { useFetchPortHolidays } from "modules/PortHolidays/useFetchPortHolidays"
import { getTurnAroundTimeMinMedianMax } from "modules/PortPredictor/helpers/utils"
import { useItineraryContext } from "modules/PortPredictor/hooks/itineraryContext"
import {
  PortPredictorTurnAroundTimeData,
  PortPredictorVesselStateDataWithMajorCargo,
} from "modules/PortPredictor/hooks/usePortPredictorAPI"
import {
  PortPredictorMarkerArrival,
  PortType,
} from "modules/PortPredictor/portPredictorReducer"
import moment from "moment"
import { Dispatch, SetStateAction } from "react"
import { Accordion, Button, OverlayTrigger } from "react-bootstrap"
import { VesselSegment } from "user/userService"
import { getPortTurnAroundTimeOptions } from "./getPortTurnAroundTimeOptions"

import {
  findNearbyHolidaysForEtaEtd,
  getActivityFromPortType,
  getArrivalDateClass,
  getLaycanWarningBadge,
  getPortType,
  renderDaysSinceArrivalAlert,
  renderHolidayWarning,
  renderTooltipForLast10,
  renderWarningForLast10Vessels,
} from "./PortPredictorDisplayArrivalHelpers"
import BigNumber from "bignumber.js"

interface PortPredictorArrivalDisplayProps {
  currMarker: PortPredictorMarkerArrival
  revertPortToOpenSea: () => void
  setIsPortTurnAroundTimeModalOpen: Dispatch<SetStateAction<boolean>>
  turnAroundTimeData?: PortPredictorTurnAroundTimeData[] | null | undefined
  segment: VesselSegment
  chosenVesselStateData: PortPredictorVesselStateDataWithMajorCargo | undefined
}

export function PortPredictorArrivalDisplay({
  currMarker,
  revertPortToOpenSea,
  turnAroundTimeData,
  setIsPortTurnAroundTimeModalOpen,
  segment,
  chosenVesselStateData,
}: PortPredictorArrivalDisplayProps) {
  const { getTabStateKeyValue } = useItineraryContext()
  const isStartOfJourney = currMarker.id === 0

  const turnAroundTimeMinMedianMax =
    getTurnAroundTimeMinMedianMax(turnAroundTimeData)

  const arrivalDate = currMarker.arrivalDate && moment(currMarker.arrivalDate)
  const today = moment(new Date())

  const daysSinceArrival = arrivalDate && today.diff(arrivalDate, "days")

  const { portHolidaysData } = useFetchPortHolidays({
    locode: currMarker.port?.locode,
    startDate:
      currMarker.arrivalDate &&
      moment(currMarker.arrivalDate).startOf("year").format("YYYY-MM-DD"),
    endDate:
      currMarker.arrivalDate &&
      moment(currMarker.arrivalDate)
        .add(1, "year")
        .endOf("year")
        .format("YYYY-MM-DD"),
  })

  const holidaysNearETA = findNearbyHolidaysForEtaEtd({
    arrivalDate: currMarker.arrivalDate,
    holidays: portHolidaysData?.holidays,
    turnAroundTime: currMarker.turnAroundTime,
  })

  const arrivalDateClass = getArrivalDateClass({
    laycanStart: currMarker.laycanStart,
    laycanEnd: currMarker.laycanEnd,
    arrivalDate: currMarker.arrivalDate,
  })

  return (
    <div className="PortPredictor_Display mb-5">
      <div className="d-flex">
        <span className="me-1">Arrival:</span>
        <span className={`me-1 fw-bold ${arrivalDateClass}`}>
          {currMarker.arrivalDate &&
            moment(currMarker.arrivalDate).format("D MMM YYYY, ddd")}
        </span>
        {currMarker.portType !== PortType.Other &&
          holidaysNearETA &&
          holidaysNearETA.length > 0 && (
            <div>
              <OverlayTrigger
                placement="bottom"
                overlay={renderHolidayWarning()}
              >
                <div>
                  <i className="ti-alert me-1 PortPredictor_AlertWarning" />
                </div>
              </OverlayTrigger>
            </div>
          )}
        {currMarker.portType &&
          currMarker.laycanEnd &&
          currMarker.laycanStart &&
          currMarker.arrivalDate &&
          getLaycanWarningBadge({
            portType: currMarker.portType,
            laycanStart: currMarker.laycanStart,
            laycanEnd: currMarker.laycanEnd,
            arrivalDate: currMarker.arrivalDate,
          })}
      </div>
      {currMarker.id === 0 && (
        <div className="d-flex">
          <div>
            {currMarker.id === 0 &&
              chosenVesselStateData?.pos_state === "Open Sea" && (
                <Accordion>
                  <Accordion.Item
                    eventKey={`${currMarker.id}_${currMarker.arrivalDate}_${currMarker.vesselPosition}`}
                  >
                    <Accordion.Header>
                      {getPortType(currMarker.portType)}
                    </Accordion.Header>
                    <Accordion.Body>
                      <div className="flex-fill" style={{ maxWidth: "250px" }}>
                        <div
                          onClick={() => {
                            if (getTabStateKeyValue("isEditMode")) {
                              revertPortToOpenSea()
                            }
                          }}
                        >
                          Change to Open Sea
                        </div>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              )}
            <div className="mb-1">{currMarker.lastNavStaDesc}</div>
          </div>
        </div>
      )}
      {isStartOfJourney && (
        <div className="d-flex mt-2">
          {daysSinceArrival && daysSinceArrival > 0 && (
            <>
              <span className="me-1">Days since arrival:</span>
              <span>{daysSinceArrival} days</span>
            </>
          )}
          {renderDaysSinceArrivalAlert(daysSinceArrival)}
          {turnAroundTimeMinMedianMax &&
            daysSinceArrival &&
            daysSinceArrival >
              Number(turnAroundTimeMinMedianMax.median.value) && (
              <div>
                <span className="ms-1" style={{ color: "#f14725" }}>
                  {BigNumber(
                    daysSinceArrival - turnAroundTimeMinMedianMax.median.value
                  )
                    .decimalPlaces(1, BigNumber.ROUND_CEIL)
                    .toFixed()}{" "}
                  days{" "}
                </span>
                <span>longer than usual</span>
              </div>
            )}
        </div>
      )}
      {turnAroundTimeData &&
        currMarker.portType &&
        currMarker.portType !== PortType.Other &&
        currMarker.cargo &&
        currMarker.port &&
        currMarker.port.port && (
          <div className="PortPredictor_TurnAroundTimeChart PortPredictor_InputWrapper mt-1">
            <div className="d-flex align-items-center flex-column">
              <div className="mt-1 d-flex justify-content-center">
                <OverlayTrigger
                  placement="bottom"
                  overlay={renderTooltipForLast10({
                    segment,
                    portType: getActivityFromPortType(currMarker.portType),
                    cargo: currMarker.cargo,
                    portName: currMarker.port?.port,
                    terminalName: currMarker.berth?.terminal_name,
                  })}
                >
                  <div className="d-flex justify-content-center align-items-center me-2">
                    <i className="PortPredictor_MinMaxMedianInfoIcon ti-info-alt" />
                  </div>
                </OverlayTrigger>
                <span className="me-1">Last 10 vessels </span>
                <Button
                  className="d-flex justify-content-center align-items-center p-1"
                  onClick={() => {
                    setIsPortTurnAroundTimeModalOpen(true)
                  }}
                  disabled={!getTabStateKeyValue("isEditMode")}
                >
                  <i
                    className="ti-fullscreen m-0"
                    style={{ fontSize: "10px" }}
                  />
                </Button>
              </div>
              <div className="d-flex">
                <span className="me-1">
                  ({currMarker.port.port}){" "}
                  {currMarker.port.terminal_name &&
                    `- ${currMarker.port.terminal_name}`}
                </span>
                <div>
                  {renderWarningForLast10Vessels({
                    turnAroundTimeData,
                    turnAroundTimeMinMedianMax,
                  })}
                </div>
              </div>
            </div>
            <ReactEcharts
              style={{
                height: "300px",
                width: "550px",
              }}
              option={getPortTurnAroundTimeOptions(turnAroundTimeData)}
            />
          </div>
        )}
    </div>
  )
}
